/**
 * The till's view of the deals catalogue (F-DEAL-N1..N4): one cached query
 * (persisted for offline, see persistedQueries.ts) plus an index the counter
 * and the Server tablet price and suggest from.
 *
 * Display only. The server re-prices every line (invariant #11); this exists
 * so the screen shows the price the server will charge.
 */
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { MenuRules, ComboComponentView, UpsellEvent } from '@/types/deals'
import type { ModifierGroup, ModifierOption } from '@/types'
import {
  availableNow,
  clockAt,
  describeWindows,
  resolveBasePrice,
  type MenuClock,
  type PriceChoice,
  type TimeWindow,
} from '@/lib/menuRules'
import { evaluateAutoDiscounts, type AutoDiscountHint, type AutoDiscountResult, type DiscountLine } from '@/lib/autoDiscounts'

export const MENU_RULES_QUERY_KEY = ['menu-rules'] as const

const EMPTY_RULES: MenuRules = {
  price_rules: {},
  availability_windows: [],
  combos: {},
  combo_choices: {},
  auto_discounts: [],
  upsell: {
    config: { enabled: false, till: true, online: true, deal_upgrade: true },
    product_modes: {},
    product_prompts: {},
    category_prompts: {},
    product_upsells: {},
    category_upsells: {},
    combo_component_of: {},
  },
  utc_offset_minutes: 300,
  server_now: '',
}

export function useMenuRules(): MenuRules {
  const q = useQuery({
    queryKey: MENU_RULES_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient.getMenuRules()
      return res.data ?? EMPTY_RULES
    },
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  })
  return q.data ?? EMPTY_RULES
}

/** Business-local clock, re-read every 30 s so windows open and close on their own. */
export function useMenuClock(utcOffsetMinutes: number): MenuClock {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(id)
  }, [])
  return useMemo(() => clockAt(now, utcOffsetMinutes), [now, utcOffsetMinutes])
}

export interface UpsellSuggestion {
  prompt: string
  productIds: string[]
}

export interface MenuRulesIndex {
  /** The product's base price for this order type now (list price when no rule applies). */
  /**
   * `explicitOnly`: a marketplace-settled order type (Foodpanda by hand) —
   * only rules naming that order type apply, as on the server.
   */
  basePrice(product: { id: string; price: number }, orderType: string, online?: boolean, explicitOnly?: boolean): PriceChoice
  available(product: { id: string; category_id?: string | null }): boolean
  /** "12:00 PM – 4:00 PM" when the product (or its category) has hours. */
  hoursLabel(product: { id: string; category_id?: string | null }): string
  isCombo(productId: string): boolean
  comboParts(productId: string): ComboComponentView[]
  /** A deal choice whose product is sold out. */
  choiceSoldOut(optionId: string): boolean
  /** A deal whose fixed item is sold out. */
  comboSoldOut(productId: string): boolean
  autoDiscount(lines: DiscountLine[], orderType: string, channel?: 'till' | 'online', explicitOnly?: boolean): { best: AutoDiscountResult | null; hints: AutoDiscountHint[] }
  upsellFor(productId: string, categoryId: string | null | undefined): UpsellSuggestion | null
  dealUpgradesFor(productId: string): string[]
  upsellEnabled: boolean
  dealUpgradeEnabled: boolean
  clock: MenuClock
}

export function buildMenuRulesIndex(rules: MenuRules, clock: MenuClock): MenuRulesIndex {
  const productWindows = new Map<string, TimeWindow[]>()
  const categoryWindows = new Map<string, TimeWindow[]>()
  for (const w of rules.availability_windows ?? []) {
    if (w.product_id) productWindows.set(w.product_id, [...(productWindows.get(w.product_id) ?? []), w])
    if (w.category_id) categoryWindows.set(w.category_id, [...(categoryWindows.get(w.category_id) ?? []), w])
  }
  const cfg = rules.upsell?.config
  const upsellEnabled = Boolean(cfg?.enabled && cfg?.till)
  return {
    clock,
    upsellEnabled,
    dealUpgradeEnabled: upsellEnabled && Boolean(cfg?.deal_upgrade),
    basePrice(product, orderType, online = false, explicitOnly = false) {
      let rs = rules.price_rules?.[product.id]
      if (explicitOnly && rs) rs = rs.filter((r) => (r.order_types ?? []).includes(orderType))
      return resolveBasePrice(product.price, rs, { orderType, online }, clock)
    },
    available(product) {
      return availableNow(
        productWindows.get(product.id),
        product.category_id ? categoryWindows.get(product.category_id) : undefined,
        clock,
      )
    },
    hoursLabel(product) {
      const ws = [
        ...(productWindows.get(product.id) ?? []),
        ...(product.category_id ? categoryWindows.get(product.category_id) ?? [] : []),
      ]
      return describeWindows(ws)
    },
    isCombo(productId) {
      return (rules.combos?.[productId]?.length ?? 0) > 0
    },
    comboParts(productId) {
      return rules.combos?.[productId] ?? []
    },
    choiceSoldOut(optionId) {
      return rules.combo_choices?.[optionId]?.is_available === false
    },
    comboSoldOut(productId) {
      return (rules.combos?.[productId] ?? []).some((p) => !p.is_available)
    },
    autoDiscount(lines, orderType, channel = 'till', explicitOnly = false) {
      let ads = rules.auto_discounts ?? []
      if (explicitOnly) ads = ads.filter((r) => (r.order_types ?? []).includes(orderType))
      if (ads.length === 0) return { best: null, hints: [] }
      return evaluateAutoDiscounts(ads, lines, { order_type: orderType, channel, clock })
    },
    upsellFor(productId, categoryId) {
      if (!upsellEnabled) return null
      const u = rules.upsell
      const mode = u.product_modes?.[productId] ?? 'inherit'
      if (mode === 'off') return null
      let ids = u.product_upsells?.[productId] ?? []
      if (ids.length === 0 && categoryId) ids = u.category_upsells?.[categoryId] ?? []
      ids = ids.filter((id) => id !== productId)
      if (ids.length === 0) return null
      const prompt =
        u.product_prompts?.[productId] || (categoryId ? u.category_prompts?.[categoryId] : '') || 'Goes well with'
      return { prompt, productIds: ids.slice(0, 4) }
    },
    dealUpgradesFor(productId) {
      if (!upsellEnabled || !cfg?.deal_upgrade) return []
      return rules.upsell?.combo_component_of?.[productId] ?? []
    },
  }
}

/** Fire-and-forget upsell analytics. Never throws, never blocks the till. */
export function logUpsellEvents(events: UpsellEvent[]): void {
  if (events.length === 0) return
  void apiClient.logUpsellEvents(events).catch(() => undefined)
}

/**
 * Mark deal choices whose product is sold out as unavailable, so the options
 * sheet strikes them through. Returns the SAME array when nothing changes —
 * the sheet resets its picks when its groups change identity.
 */
export function markSoldOutDealChoices(groups: ModifierGroup[], soldOut: (optionId: string) => boolean): ModifierGroup[] {
  const hit = (g: ModifierGroup) => (g.options ?? []).some((o) => o.is_available && soldOut(o.id))
  if (!groups.some(hit)) return groups
  return groups.map((g) =>
    hit(g)
      ? { ...g, options: (g.options ?? []).map((o) => (o.is_available && soldOut(o.id) ? { ...o, is_available: false } : o)) }
      : g,
  )
}

export function markSoldOutDealChoiceMap(
  map: ReadonlyMap<string, ModifierOption>,
  soldOut: (optionId: string) => boolean,
): ReadonlyMap<string, ModifierOption> {
  let changed = false
  const out = new Map<string, ModifierOption>()
  for (const [id, o] of map) {
    if (o.is_available && soldOut(id)) {
      changed = true
      out.set(id, { ...o, is_available: false })
    } else out.set(id, o)
  }
  return changed ? out : map
}
